import {
  faCalendarDay,
  faCheck,
  faChevronCircleLeft,
  faPen,
} from '@fortawesome/free-solid-svg-icons';
import { faUserCircle } from '@fortawesome/free-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Timestamp } from 'firebase/firestore';
import { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Content from '../../../components/layout/Content';
import TopBar from '../../../components/layout/TopBar';
import SelectOutlet from '../../../components/routes/SelectOutlet';
import Alert from '../../../components/ui/Alert';
import Back from '../../../components/ui/Back';
import Button from '../../../components/ui/Button';
import Loader from '../../../components/ui/Loader';
import SortableList from '../../../components/ui/SortableList';
import { useCheckAccess } from '../../../lib/auth/use-checkAccess';
import { useRoute } from '../../../lib/hooks/use-routes';
import ChangeRoute from '../../../components/routes/ChangeRoute';
import { useTempRoute } from '../../../lib/hooks/use-temproutes';
import { useEffect } from 'react';
import { Confirm } from '../../../components/forms/Confirm';
import { RouteCreateData } from '../../../lib/db/types';

type RouteParams = 'id';

const EditRoutePage = () => {
  const { id } = useParams<RouteParams>();
  const {
    route,
    deleteRoute,
    error,
    loading,
    outlets,
    update,
    addOutlet,
    removeOutlet,
  } = useRoute(id);
  const navigate = useNavigate();
  const {
    tempRoute,
    error: tempError,
    loading: tempLoading,
    tempOutlets,
    create: tempCreate,
    update: tempUpdate,
    addTempOutlet,
    deleteTempRoute,
    removeOutlet: tempRemoveOutlet,
    loadOutlets: tempLoadOutlets,
    updateWeight: tempUpdateWeight,
  } = useTempRoute(id);
  const [showAlert, setShowAlert] = useState(false);
  const checkAccess = useCheckAccess();
  const [showSelectOutlet, setShowSelectOutlet] = useState(false);
  const [showChangeName, setShowChangeName] = useState(false);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [showAcceptChangesConfirmation, setShowAcceptChangesConfirmation] =
    useState(false);
  const [showRejectChangesConfirmation, setShowRejectChangesConfirmation] =
    useState(false);
  const [showAcceptChanges, setShowAcceptChanges] = useState(false);
  const [showSaveChangesConfirmation, setShowSaveChangesConfirmation] =
    useState(false);

  useEffect(() => {
    if (route) {
      if (route.lastEditStatus && !route.changeConfirmed) {
        setShowAcceptChanges(true);
      }
    }
  }, [route]);

  useEffect(() => {
    if (!tempRoute && !route && !tempLoading && !loading) {
      navigate('/routes/');
    }
  }, [route, loading, tempRoute, tempLoading]);

  useEffect(() => {
    if (tempRoute && !tempRoute.readyForEdit && outlets && tempOutlets) {
      for (let outlet of outlets) {
        if (
          !tempOutlets.find((el) => {
            return (el.id = outlet.id);
          })
        ) {
          addTempOutlet(outlet);
        }
      }
      tempUpdate({ readyForEdit: true });
      tempLoadOutlets();
    }
  }, [outlets, tempRoute, tempOutlets]);

  useEffect(() => {
    if (!tempLoading && !loading && !tempRoute && route) {
      setShowConfirmation(true);
    } else {
      setShowConfirmation(false);
    }
  }, [loading, route, tempLoading, tempRoute]);

  const add = async (data) => {
    await tempUpdate({
      outlets: tempOutlets.length + data.length,
      edited: true,
    });

    let i = 1;
    for (let outlet of data) {
      await addTempOutlet({
        id: outlet.outletId,
        name: outlet.name,
        weight: route.outlets ? route.outlets + i : 1,
        createdAt: Timestamp.now(),
        retailerId: outlet.retailerId,
      });
      i++;
    }

    await update({ edited: true });
    tempLoadOutlets();
  };

  const changeRouteName = async (data) => {
    setShowChangeName(false);

    if (data.fsa) {
      await tempUpdate({
        name: data.routeName,
        fsa: data.fsa,
        defaultDay: data.defaultDay,
        defaultDayNumber: data.defaultDayNumber,
        edited: true,
      });
    } else {
      await tempUpdate({
        name: data.routeName,
        defaultDay: data.defaultDay,
        defaultDayNumber: data.defaultDayNumber,
        edited: true,
      });
    }
    await update({ edited: true });
  };

  const removeOutletFromRoute = async (outletId) => {
    await tempUpdate({ outlets: tempOutlets.length - 1, edited: true });
    await update({ edited: true });

    await tempRemoveOutlet(outletId);

    tempLoadOutlets();
  };

  const moveHigher = async (outletId) => {
    let outletsArray = tempOutlets;

    let index = outletsArray.findIndex((outlet) => {
      return outlet.id === outletId;
    });
    let oldWeight = outletsArray[index].weight;
    let otherOutlet = outletsArray[index - 1];

    await tempUpdateWeight(outletId, oldWeight - 1);
    await tempUpdateWeight(otherOutlet.id, oldWeight);

    await tempUpdate({ edited: true });
    await update({ edited: true });
    tempLoadOutlets();
  };

  const moveLower = async (outletId) => {
    let outletsArray = tempOutlets;

    let index = outletsArray.findIndex((outlet) => {
      return outlet.id === outletId;
    });

    let oldWeight = outletsArray[index].weight;
    let otherOutlet = outletsArray[index + 1];

    await tempUpdateWeight(outletId, oldWeight + 1);
    await tempUpdateWeight(otherOutlet.id, oldWeight);

    await tempUpdate({ edited: true });
    await update({ edited: true });
    tempLoadOutlets();
  };

  const handleOnCancel = () => {
    setShowConfirmation(false);
  };

  const handleOnConfirm = async () => {
    if (!tempRoute) {
      await tempCreate(
        { ...route, readyForEdit: false } as RouteCreateData,
        route.id
      );

      await tempLoadOutlets();
    }
  };

  const handleOnAcceptChanges = async () => {
    await update({
      name: tempRoute.name,
      defaultDay: tempRoute.defaultDay,
      defaultDayNumber: tempRoute.defaultDayNumber,
      fsa: tempRoute.fsa,
      outlets: tempRoute.outlets,
      sop: tempRoute.sop,
      userIds: tempRoute.userIds,
      edited: false,
      lastEditStatus: 'accepted',
      changeConfirmed: false,
      newRoute: false,
    });

    for (let outlet of outlets) {
      await removeOutlet(outlet.id);
    }

    for (let outlet of tempOutlets) {
      await addOutlet({
        id: outlet.id,
        name: outlet.name,
        weight: outlet.weight,
        createdAt: Timestamp.now(),
        retailerId: outlet.retailerId,
      });
    }

    await deleteTempRoute();

    setShowAcceptChangesConfirmation(false);
    navigate('/routes/' + id);
  };

  const handleOnRejectChanges = async () => {
    if (route.newRoute) {
      await deleteRoute();
    } else {
      await update({
        lastEditStatus: 'rejected',
        changeConfirmed: false,
        edited: false,
      });
    }
    await deleteTempRoute();

    setShowRejectChangesConfirmation(false);
    navigate('/routes/');
  };

  const handleOnConfirmChanges = async () => {
    await update({ changeConfirmed: true });
    setShowConfirmation(false);
    navigate('/routes/' + id);
  };
  const handleOnCancelChanges = async () => {
    await update({ changeConfirmed: true });
    setShowConfirmation(false);
    navigate('/routes/' + id);
  };

  const handleOnConfirmSaveChanges = async () => {
    await update({ edited: true });
    setShowSaveChangesConfirmation(false);
    navigate('/routes/' + id);
  };
  const handleOnCancelSaveChanges = async () => {
    await update({
      edited: false,
    });

    await deleteTempRoute();

    setShowSaveChangesConfirmation(false);
    navigate('/routes/' + id);
  };

  return (
    <Content topBar>
      <Loader show={loading} />
      {checkAccess(['fsa']) && route && !route.changeConfirmed && (
        <Confirm
          title={`Your changes on this route have been ${route.lastEditStatus}.`}
          open={showAcceptChanges}
          onConfirm={handleOnConfirmChanges}
          onCancel={handleOnCancelChanges}
          setOpen={(open) => {
            setShowAcceptChanges(open);
          }}
          oneButtonText="Confirm"
        />
      )}
      <Confirm
        title={'Do your really want to edit this route?'}
        open={showConfirmation && !showAcceptChanges}
        onConfirm={handleOnConfirm}
        onCancel={handleOnCancel}
        setOpen={(open) => {
          setShowConfirmation(open);
        }}
      />
      <Confirm
        title={'Do want to save your changes?'}
        open={showSaveChangesConfirmation}
        onConfirm={handleOnConfirmSaveChanges}
        onCancel={handleOnCancelSaveChanges}
        setOpen={(open) => {
          setShowSaveChangesConfirmation(open);
        }}
      />
      <Confirm
        title={'Do you want to accept these changes?'}
        open={showAcceptChangesConfirmation}
        onConfirm={handleOnAcceptChanges}
        onCancel={() => setShowAcceptChangesConfirmation(false)}
        setOpen={(open) => {
          setShowAcceptChangesConfirmation(open);
        }}
      />
      <Confirm
        title={'Do you want to reject these changes?'}
        open={showRejectChangesConfirmation}
        onConfirm={handleOnRejectChanges}
        onCancel={() => setShowRejectChangesConfirmation(false)}
        setOpen={(open) => {
          setShowRejectChangesConfirmation(open);
        }}
      />
      {checkAccess(['sop']) && (
        <SelectOutlet
          open={showSelectOutlet}
          onAddOutlet={add}
          setOpen={(open) => setShowSelectOutlet(open)}
          confirmText="Are you sure you want to add this outlet to this route?"
          buttonText="Add outlet to route"
          routeOutlets={outlets}
        />
      )}
      {checkAccess(['fsa']) && (
        <SelectOutlet
          open={showSelectOutlet}
          onAddOutlet={add}
          setOpen={(open) => setShowSelectOutlet(open)}
          confirmText="Are you sure you want to add this outlet to this route?"
          buttonText="Add outlet to route"
          routeOutlets={tempOutlets}
        />
      )}
      {checkAccess(['sop']) && route && (
        <ChangeRoute
          open={showChangeName}
          onChangeRouteName={changeRouteName}
          setOpen={(open) => setShowChangeName(open)}
          confirmText="Are you sure you want to update this route?"
          buttonText="Save route changes"
          routeName={route.name}
          fsaId={route.fsa.id}
          defaultDay={route.defaultDay}
        />
      )}
      {checkAccess(['fsa']) && route && (
        <ChangeRoute
          open={showChangeName}
          onChangeRouteName={changeRouteName}
          setOpen={(open) => setShowChangeName(open)}
          confirmText="Are you sure you want to update this route?"
          buttonText="Save route changes"
          routeName={tempRoute ? tempRoute.name : route.name}
          fsaId={tempRoute ? tempRoute.fsa.id : route.fsa.id}
          defaultDay={tempRoute ? tempRoute.defaultDay : route.defaultDay}
        />
      )}
      {error && (
        <Alert
          title="Error"
          message={error.message}
          open={showAlert}
          setOpen={(open) => setShowAlert(open)}
        />
      )}
      <TopBar>
        <div className="w-full grid col-span-11 grid-cols-11 justify-center mt-4">
          <div className={`flex my-3 mr-2 col-span-3`}>
            <button
              onClick={() => setShowSaveChangesConfirmation(true)}
              className="pl-3 pr-4 py-2 "
            >
              <FontAwesomeIcon
                icon={faChevronCircleLeft}
                className="text-xl text-lh-dark-blue"
              />
            </button>
          </div>
          <div className="col-span-5 flex justify-center items-center text-center text-xl font-bold text-lh-head-black">
            <p className="cursor-pointer font-bold">
              {tempRoute ? tempRoute.name : ''}
            </p>
          </div>
          <div className={`flex my-3 mr-2 col-span-3 flex-row-reverse`}>
            {(checkAccess(['sop']) && route && !route.edited) ||
            checkAccess(['fsa']) ? (
              <button
                onClick={() => setShowChangeName(true)}
                className="pl-4 pr-3 py-2 "
              >
                <FontAwesomeIcon
                  icon={faPen}
                  className="text-xl text-lh-dark-blue"
                />
              </button>
            ) : (
              ''
            )}
          </div>
          <h2 className="col-span-5 text-center font-bold text-lg text-lh-head-black">
            <FontAwesomeIcon
              icon={faUserCircle}
              className="mr-2 text-lh-head-black"
            />
            {tempRoute ? tempRoute.fsa.name : ''}
          </h2>
          <p className="col-span-5 col-start-7 font-bold text-center text-lg text-lh-text-black">
            <FontAwesomeIcon
              icon={faCalendarDay}
              className="mr-2 text-lh-head-black"
            />
            {tempRoute && tempRoute.defaultDay
              ? tempRoute.defaultDay
              : 'No default day'}
          </p>
        </div>
      </TopBar>
      {route && tempOutlets && tempOutlets.length > 0 ? (
        <SortableList
          items={tempOutlets}
          type="routes"
          loading={loading}
          removeOutletFunction={removeOutletFromRoute}
          moveHigherFunction={moveHigher}
          moveLowerFunction={moveLower}
          tempRoute={true}
          routeEdited={route && route.edited ? true : false}
        />
      ) : (
        <p className="w-60 justify-between mx-2 mb-2 mt-10 h-18 list-none pb-4 px-2 pt-2 bg-white text-center">
          No outlets yet.
        </p>
      )}
      {checkAccess(['fsa']) && (
        <Button
          onClick={() => setShowSelectOutlet(true)}
          className={
            'rounded-full h-20 w-20 flex items-center justify-center bg-green-500 fixed text-5xl bottom-5 left-1/2 -ml-10'
          }
          color="green"
        >
          +
        </Button>
      )}
      {checkAccess(['sop']) && (
        <>
          <Button
            onClick={() => setShowAcceptChangesConfirmation(true)}
            className={
              'rounded-full h-20 w-20 flex items-center justify-center bg-green-500 fixed text-5xl bottom-5 left-1/2 -ml-20'
            }
            color="green"
          >
            <FontAwesomeIcon icon={faCheck} />
          </Button>
          <Button
            onClick={() => setShowRejectChangesConfirmation(true)}
            className={
              'rounded-full h-20 w-20 flex items-center justify-center bg-green-500 fixed text-5xl bottom-5 left-1/2 ml-0'
            }
            color="red"
          >
            X
          </Button>
        </>
      )}
    </Content>
  );
};

export default EditRoutePage;
